import { ElementRef, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { translate } from '@vimeo/i18n-runtime';

import T from '../../../translate';
import { useFeatureFlow } from '../../contexts/FeatureFlowContext';
import {
  useLead,
  useNotification,
  useRefreshConfirmationPage,
  useRouterPath,
  useViewerAnalytics,
} from '../../hooks';
import { useGlobalStore } from '../../state/globalStore';
import { Padding } from '../../styles/commonStyle';
import {
  BUTTON_ROLES,
  BUTTON_TEXT_TYPE,
  FEATURES,
  FORM_FIELD_NAME_VALUES,
  FORM_LAYOUT_OPTIONS,
  KEY_CODES,
  LOGIN_ERROR_CODES,
  PREVIEW_FORM_FIELDS_WIDTH,
  PREVIEW_TYPE,
  REGISTRATION_FULL_ERROR_CODE,
} from '../../utils/constants';
import { getIdFromLeadUri, getSourceUrl, setCookie } from '../../utils/helper';
import { FieldOption } from '../../utils/types';
import { AgreeCheckbox } from '../common/AgreeCheckbox';
import { EventDate } from '../common/EventDate';
import { Checkbox, Dropdown, Input } from '../elements';
import { ProgressBar } from '../elements/ProgressBar';
import { Alignment, Logo, WrapperBox } from '../previewStyles';
import { StylableButton } from '../stylable-button';
import { StylableText } from '../stylable-text';
import { CurrentFieldItem } from './CurrentFieldItem';

export const RegistrationView = () => {
  const leadCaptureProperties = useGlobalStore((state) => state.leadCapture);
  const { customFields: fields } = leadCaptureProperties;
  const {
    alignment,
    layout,
    joinPageTitle,
    logo,
    nonEditableTextStyle: { alreadyRegistered: alreadyRegisteredStyle },
  } = leadCaptureProperties;
  const [prefillFields, setPrefillFields] = useState(
    fields.filter((field) => field.default).map((field) => field.id),
  );
  const registrationTitleRef = useRef<ElementRef<typeof StylableText>>(null);
  const {
    setCurrentSelectedFieldIndex,
    currentSelectedFieldIndex,
    setEmailAddressCached,
    emailAddressCached,
  } = useGlobalStore((store) => store);
  const lastSelectedIndex = useRef(currentSelectedFieldIndex);
  lastSelectedIndex.current = currentSelectedFieldIndex;
  const singleStepFieldsRef = useRef<
    (ElementRef<typeof Input> | ElementRef<typeof Dropdown> | ElementRef<typeof Checkbox> | null)[]
  >([]);
  const multiStepFieldsRef = useRef<
    (ElementRef<typeof Input> | ElementRef<typeof Dropdown> | ElementRef<typeof Checkbox> | null)[]
  >([]);
  const [agreeCheckboxChecked, setAgreeCheckboxChecked] = useState(false);
  const [agreeCheckboxNotCheckedError, setAgreeCheckboxNotCheckedError] = useState(false);
  const [isValidatingEmail, setIsValidatingEmail] = useState(false);
  const [apiError, setApiError] = useState<string>();
  const agreeCheckboxRef = useRef<ElementRef<typeof AgreeCheckbox>>(null);
  const { putEntityEventLead, loginEntityLead } = useLead();
  const { show: showNotification } = useNotification();
  const { refresh: refreshConfirmationPageData } = useRefreshConfirmationPage();
  const [isLoading, setIsLoading] = useState(false);
  const [autoFocus, setAutoFocus] = useState(false);
  const { onPreviewChange } = useRouterPath();
  const { sendViewerBpEvent } = useViewerAnalytics();
  const multiStepItemWrapperRefs = useRef<(HTMLDivElement | null)[]>([]);
  const hasConfirmationView = useFeatureFlow(FEATURES.CONFIRMATION_VIEW);

  const [inputData, setInputData] = useState<
    { cid: number; value: boolean | string | FieldOption | undefined }[]
  >([]);
  const { apiEntityId: entityId } = useGlobalStore((store) => store);

  const progressBarPercent = ((currentSelectedFieldIndex + 1) / fields.length) * 100;
  const animationDirection = useRef<'up' | 'down'>('up');

  const saveInputValue = (value: boolean | string | FieldOption | undefined, cid: number) => {
    const index = inputData.findIndex((v) => v.cid === cid);
    const data = [...inputData];
    if (index >= 0) {
      data[index] = { cid, value };
    } else {
      data.push({ cid, value });
    }
    setInputData(data);
    // Save the email address to zustand so available across multiple screen
    const fieldInfo = fields.find((f) => f.cid === cid);
    if (fieldInfo?.name === FORM_FIELD_NAME_VALUES.EMAIL) {
      setEmailAddressCached(value as string);
    }
  };

  const getInputValue = (index: number) =>
    inputData.find((v) => v.cid === fields[index].cid)?.value;

  const onNextClick = async () => {
    const ref = multiStepFieldsRef.current[currentSelectedFieldIndex];
    const currentField = fields[currentSelectedFieldIndex];
    if (prefillFields.includes(currentField.id)) {
      animationDirection.current = 'up';
      setCurrentSelectedFieldIndex(currentSelectedFieldIndex + 1);
      return;
    }
    setApiError(undefined);
    if (ref?.validate()) {
      if (currentField.name === FORM_FIELD_NAME_VALUES.EMAIL) {
        setIsValidatingEmail(true);
        const emailAddress = ref.getValue();
        const loginResponse = await loginEntityLead({ email: emailAddress });
        if (loginResponse && loginResponse.errorCode === LOGIN_ERROR_CODES.INVALID_EMAIL) {
          setApiError(T.InvalidEmailAddress);
        } else {
          animationDirection.current = 'up';
          setCurrentSelectedFieldIndex(currentSelectedFieldIndex + 1);
        }
        setIsValidatingEmail(false);
      } else {
        animationDirection.current = 'up';
        setCurrentSelectedFieldIndex(currentSelectedFieldIndex + 1);
      }
    }
  };

  const onBackClick = () => {
    animationDirection.current = 'down';
    setCurrentSelectedFieldIndex(currentSelectedFieldIndex - 1);
  };

  const saveLead = async () => {
    const firstNameCid = fields.find((v) => v.name === FORM_FIELD_NAME_VALUES.FIRST_NAME)?.cid;
    const lastNameCid = fields.find((v) => v.name === FORM_FIELD_NAME_VALUES.LAST_NAME)?.cid;
    const emailCid = fields.find((v) => v.name === FORM_FIELD_NAME_VALUES.EMAIL)?.cid;
    const firstName = inputData.find((v) => v.cid === firstNameCid)?.value as string;
    const lastName = inputData.find((v) => v.cid === lastNameCid)?.value as string;
    const email = inputData.find((v) => v.cid === emailCid)?.value as string;

    const getInputValue = (cid: number) => {
      const value = inputData.find((d) => d.cid === cid)?.value;
      if (typeof value === 'string' || typeof value === 'boolean') {
        return value;
      } else {
        return value?.optionLabel;
      }
    };

    const mainColumnsFieldKeys = [
      FORM_FIELD_NAME_VALUES.EMAIL,
      FORM_FIELD_NAME_VALUES.FIRST_NAME,
      FORM_FIELD_NAME_VALUES.LAST_NAME,
    ];
    const customData = fields
      .filter((f) => !(mainColumnsFieldKeys as string[]).includes(f.name))
      .map((f) => ({ [f.name]: getInputValue(f.cid) }));
    let customDataObject = {};
    customData.forEach((data) => {
      for (const [, value] of Object.entries(data)) {
        /**
         * This loop will only iterate once because data will only have one key-value pair.
         */
        if (typeof value === 'boolean' || (value && value.length > 0)) {
          /**
           * This will ensure we don't pass empty data or empty string to API.
           */
          customDataObject = { ...customDataObject, ...data };
        }
      }
    });

    setIsLoading(true);
    setApiError(undefined);

    let response;
    /** TODO: Add parentLeadUuid to payload. */
    const payload = {
      first_name: firstName,
      last_name: lastName,
      email,
      data: JSON.stringify(customDataObject),
      source_metadata: { url: getSourceUrl() ?? '' },
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      prefill_fields: prefillFields,
    };

    response = await putEntityEventLead(payload);

    const leadId = response?.success ? getIdFromLeadUri(response?.data?.['uri']) : false;
    if (leadId) {
      if (hasConfirmationView) {
        const confirmationPageData = await refreshConfirmationPageData(leadId);
        if (confirmationPageData) {
          setCookie(entityId, leadId);
          onPreviewChange(PREVIEW_TYPE.CONFIRMATION);
          /**
           * In case of multi step form,
           *
           * If registration is successful, reset the current field index to point to the first field,
           * so in case if registrant was deleted by admin while confirmation page is kept open,
           * the registrant will see the registration page again starting from the first field.
           * Otherwise registrant will see the last field.
           */
          useGlobalStore.setState({ cookie: leadId, currentSelectedFieldIndex: 0 });
        } else {
          showNotification({ message: T.UhOhThereWasAProblem });
        }
      } else {
        setCookie(entityId, leadId);
        useGlobalStore.setState({ cookie: leadId, currentSelectedFieldIndex: 0 });
      }
    } else if (REGISTRATION_FULL_ERROR_CODE.includes(response?.error['errorCode'])) {
      onPreviewChange(PREVIEW_TYPE.FORM_FULL);
    } else if (response?.error['errorCode'] === LOGIN_ERROR_CODES.INVALID_EMAIL) {
      setApiError(T.InvalidEmailAddress);
    } else {
      showNotification({ message: T.UhOhThereWasAProblem });
    }
    setIsLoading(false);
  };

  const submitToApi = async () => {
    saveLead();
    sendViewerBpEvent({
      eventName: 'vimeo.registration_form_complete',
    });
  };

  const onSingleStepSubmit = async () => {
    const allValid = singleStepFieldsRef.current
      .map((ref) => {
        return ref?.validate();
      })
      .every((v) => v === true);
    setAgreeCheckboxNotCheckedError(!agreeCheckboxChecked);
    agreeCheckboxRef.current?.shakeError();
    if (agreeCheckboxChecked == true && allValid) {
      submitToApi();
    }
  };

  const onMultiStepSubmit = async () => {
    const lastValid = multiStepFieldsRef.current[multiStepFieldsRef.current.length - 1]?.validate();
    setAgreeCheckboxNotCheckedError(!agreeCheckboxChecked);
    agreeCheckboxRef.current?.shakeError();
    if (agreeCheckboxChecked == true && lastValid) {
      submitToApi();
    }
  };

  const onCheckboxChecked = (checked: boolean) => {
    setAgreeCheckboxChecked(checked);
    setAgreeCheckboxNotCheckedError(!checked);
  };

  const onEnterKeyPressed = (value: string | FieldOption | undefined, cid: number) => {
    if (value !== undefined && cid !== undefined) {
      saveInputValue(value, cid);
    }
    if (currentSelectedFieldIndex < fields.length - 1) {
      onNextClick();
    } else {
      onMultiStepSubmit();
    }
  };

  const enableAutoFocus = () => {
    setAutoFocus(true);
  };

  // In case something is there in the emailCached, put that to inputData so it displays by default
  useEffect(() => {
    const emailCid = fields.find((f) => f.name === FORM_FIELD_NAME_VALUES.EMAIL)?.cid;
    emailCid !== undefined &&
      setInputData([
        {
          cid: emailCid,
          value: emailAddressCached,
        },
      ]);
    // Need to run this code on initial render only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Alignment layout={layout} align={alignment} addPadding onMouseEnter={enableAutoFocus}>
        <WrapperBox paddingTop={50} paddingBottom={50} maxWidth={PREVIEW_FORM_FIELDS_WIDTH + 8}>
          {logo.isActive && (
            <a
              {...(logo.isLinkActive && logo.customLink?.length > 0
                ? { href: logo.customLink, target: '_blank' }
                : {})}
            >
              <Logo src={logo.url} />
            </a>
          )}
          <StylableText ref={registrationTitleRef} content={joinPageTitle} />
          <Padding left={4} right={4}>
            <EventDate />
          </Padding>
          <Padding left={4} right={4}>
            {layout === FORM_LAYOUT_OPTIONS.SINGLE_STEP_FORM ? (
              <SingleStepWrapper>
                {fields.map((field, index) => {
                  return (
                    <CurrentFieldItem
                      key={field.cid}
                      multiStepItemWrapperRefs={multiStepItemWrapperRefs}
                      autoFocus={autoFocus && index === 0}
                      value={getInputValue(index) ? (getInputValue(index) as string) : undefined}
                      onSaveValue={(value) => saveInputValue(value, field.cid)}
                      onEnterKeyPressed={(value) => onEnterKeyPressed(value, fields[index].cid)}
                      fieldsRef={singleStepFieldsRef.current}
                      field={fields[index]}
                      prefillField={prefillFields.includes(fields[index].cid)}
                      index={index}
                      /** Currently we only support dynamic error for email only */
                      errorMessage={field?.name === FORM_FIELD_NAME_VALUES.EMAIL ? apiError : ''}
                      onClearPrefill={() =>
                        setPrefillFields(prefillFields.filter((f) => f !== fields[index].id))
                      }
                    />
                  );
                })}
                <AgreeCheckbox
                  ref={agreeCheckboxRef}
                  error={agreeCheckboxNotCheckedError}
                  checked={agreeCheckboxChecked}
                  onChange={onCheckboxChecked}
                />
                <ButtonsWrapper>
                  <StylableButton
                    role={BUTTON_ROLES.PRIMARY}
                    onClick={onSingleStepSubmit}
                    textType={BUTTON_TEXT_TYPE.REGISTER}
                    loading={isLoading}
                    disabled={isLoading}
                  />
                </ButtonsWrapper>
                <StyledLoginTextWrapper>
                  <StyledLoginText color={alreadyRegisteredStyle.color}>
                    {translate({
                      singular: 'Already registered? {LINK}Sign in{/LINK}',
                      replacements: {
                        LINK: (text: string) => (
                          <Pointer
                            tabIndex={0}
                            onKeyPress={(e) =>
                              e.key === KEY_CODES.ENTER &&
                              onPreviewChange(PREVIEW_TYPE.LOGIN_SCREEN)
                            }
                            onClick={() => onPreviewChange(PREVIEW_TYPE.LOGIN_SCREEN)}
                          >
                            {text}
                          </Pointer>
                        ),
                      },
                    })}
                  </StyledLoginText>
                </StyledLoginTextWrapper>
              </SingleStepWrapper>
            ) : (
              <MultiStepWrapper>
                <MultiStepItemsWrapper
                  /**
                   * Why?
                   * Checkbox text can grow multiline and it will increase the height of the field. So we need to set the minheight to the currently selected field's client height.
                   * This is because the child component is absolutely positioned and it will not increase the height of the parent.
                   */
                  minHeight={
                    multiStepItemWrapperRefs.current[currentSelectedFieldIndex]?.clientHeight || 60
                  }
                >
                  <StyledAnimatedFieldDiv>
                    {fields.map((field, index) => {
                      return (
                        <CurrentFieldItem
                          key={field.cid}
                          multiStepItemWrapperRefs={multiStepItemWrapperRefs}
                          show={index === currentSelectedFieldIndex}
                          autoFocus={autoFocus && index === currentSelectedFieldIndex}
                          animationDirection={animationDirection.current}
                          value={getInputValue(index)}
                          onSaveValue={(value) => {
                            saveInputValue(value, fields[index].cid);
                          }}
                          onEnterKeyPressed={(value) => onEnterKeyPressed(value, fields[index].cid)}
                          fieldsRef={multiStepFieldsRef.current}
                          field={fields[index]}
                          prefillField={prefillFields.includes(fields[index].cid)}
                          index={index}
                          /** Currently we only support dynamic error for email only */
                          errorMessage={
                            field?.name === FORM_FIELD_NAME_VALUES.EMAIL ? apiError : ''
                          }
                          onClearPrefill={() =>
                            setPrefillFields(prefillFields.filter((f) => f !== fields[index].id))
                          }
                        />
                      );
                    })}
                  </StyledAnimatedFieldDiv>
                </MultiStepItemsWrapper>

                {currentSelectedFieldIndex === fields.length - 1 && (
                  <AgreeCheckbox
                    ref={agreeCheckboxRef}
                    checked={agreeCheckboxChecked}
                    error={agreeCheckboxNotCheckedError}
                    onChange={onCheckboxChecked}
                  />
                )}
                <ButtonsWrapper>
                  {currentSelectedFieldIndex !== 0 && (
                    <ButtonWrapper>
                      <StylableButton
                        disabled={currentSelectedFieldIndex === 0 || isLoading}
                        onClick={onBackClick}
                        role={BUTTON_ROLES.SECONDARY}
                        textType={BUTTON_TEXT_TYPE.BACK}
                      />
                    </ButtonWrapper>
                  )}
                  <ButtonWrapper>
                    {currentSelectedFieldIndex < fields.length - 1 && (
                      <StylableButton
                        role={BUTTON_ROLES.PRIMARY}
                        onClick={onNextClick}
                        textType={BUTTON_TEXT_TYPE.NEXT}
                        loading={isValidatingEmail}
                        disabled={isValidatingEmail}
                      />
                    )}
                    {currentSelectedFieldIndex === fields.length - 1 && (
                      <StylableButton
                        role={BUTTON_ROLES.PRIMARY}
                        onClick={onMultiStepSubmit}
                        textType={BUTTON_TEXT_TYPE.REGISTER}
                        loading={isLoading}
                        disabled={isLoading}
                      />
                    )}
                  </ButtonWrapper>
                </ButtonsWrapper>

                <StyledLoginTextWrapper>
                  <StyledLoginText color={alreadyRegisteredStyle.color}>
                    {translate({
                      singular: 'Already registered? {LINK}Sign in{/LINK}',
                      replacements: {
                        LINK: (text: string) => (
                          <Pointer
                            tabIndex={0}
                            onKeyPress={(e) =>
                              e.key === KEY_CODES.ENTER &&
                              onPreviewChange(PREVIEW_TYPE.LOGIN_SCREEN)
                            }
                            onClick={() => onPreviewChange(PREVIEW_TYPE.LOGIN_SCREEN)}
                          >
                            {text}
                          </Pointer>
                        ),
                      },
                    })}
                  </StyledLoginText>
                </StyledLoginTextWrapper>
              </MultiStepWrapper>
            )}
          </Padding>
        </WrapperBox>
      </Alignment>
      {layout === FORM_LAYOUT_OPTIONS.MULTI_STEP_FORM && (
        <ProgressBarWrapper>
          <StepCount>{T.StepXofY(currentSelectedFieldIndex + 1, fields.length)}</StepCount>
          <ProgressBar value={progressBarPercent} />
        </ProgressBarWrapper>
      )}
    </>
  );
};

const Pointer = styled.u`
  cursor: pointer;
`;

const StyledLoginTextWrapper = styled.div`
  padding: 0 4px;
  margin-top: 16px;
`;

const StyledLoginText = styled.span<{ color: string }>`
  font-size: 12px;
  font-weight: 400;
  color: ${({ color }) => color};
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex: 1;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 8px;
  padding-top: 6px;
`;

const StyledLabel = styled.span<{ color: string }>`
  font-size: 11px;
  font-weight: 400;
  line-height: 18px;
  line-height: 18px;
  text-align: left;
  color: ${({ color }) => color};
  display: inline-block;
`;

const StyledAnimatedFieldDiv = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
`;

const SingleStepWrapper = styled.div`
  position: relative;
  padding-top: 16px;
  padding-bottom: 16px;
  z-index: 1;
`;

const MultiStepWrapper = styled.div`
  padding-top: 8px;
`;

const MultiStepItemsWrapper = styled.div<{ minHeight: number }>`
  position: relative;
  width: 100%;
  min-height: ${({ minHeight }) => minHeight}px;
  margin-bottom: 16px;
  overflow: visible;
  z-index: 1;
`;

const FieldWrapper = styled.div`
  margin-top: 2px;
  margin-bottom: 16px;
  width: 100%;
`;

const ProgressBarWrapper = styled.div`
  pointer-events: none;
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  padding-bottom: 26px;
  padding-left: 24px;
  padding-right: 24px;
  box-sizing: border-box;
  z-index: 100;
`;

const StepCount = styled.div`
  pointer-events: none;
  padding-bottom: 8px;
  font-size: 16px;
  font-weight: 400;
  z-index: 100;
`;
